import React, { createContext, useContext, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { User } from '@supabase/supabase-js';
import { supabase } from './lib/supabase';
import Login from './components/Login';
import AdminDashboard from './components/AdminDashboard';
import MobileScanner from './components/MobileScanner';
import CountSession from './components/CountSession';
import Layout from './components/Layout';

interface AuthContextType {
  user: User | null;
  userRole: 'admin' | 'user' | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<'admin' | 'user' | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserRole(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null);
        if (session?.user) {
          fetchUserRole(session.user.id);
        } else {
          setUserRole(null);
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserRole = async (userId: string) => {
    try {
      // Use the service role or a simpler approach to avoid RLS recursion
      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user role:', error);
        // Default to user role if there's an error
        setUserRole('user');
      } else if (data) {
        setUserRole(data.role ?? 'user');
      } else {
        // No profile found, default to user
        setUserRole('user');
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
      setUserRole('user');
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setUserRole(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, userRole, loading, signOut }}>
      <div className="min-h-screen bg-base-200">
        {!user ? (
          <Login />
        ) : (
          <Layout>
            <Routes>
              <Route path="/" element={
                userRole === 'admin' ? 
                  <Navigate to="/admin" replace /> : 
                  <Navigate to="/scanner" replace />
              } />
              <Route path="/admin" element={
                userRole === 'admin' ? 
                  <AdminDashboard /> : 
                  <Navigate to="/scanner" replace />
              } />
              <Route path="/scanner" element={<MobileScanner />} />
              <Route path="/count/:sessionId" element={<CountSession />} />
            </Routes>
          </Layout>
        )}
      </div>
    </AuthContext.Provider>
  );
}

export default App; 